import React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Page as PageLayout,
  Button,
  Text,
  Flex,
  LoadingOrbit,
  EmptyState,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TagBadge,
  StatusBadge,
  normalizeRunStatus,
  ListCard,
  ListCardRow,
  Menu,
  MenuItem,
  MenuTrigger,
  MenuSeparator,
  useToast,
  type StatusName,
} from '@chatsdet/ui';
import {
  Stack,
  Plus,
  Play,
  PencilSimple,
  Trash,
  DotsThreeVertical,
  GithubLogo,
  UploadSimple,
} from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { formatRelativeTime } from '../FlowDesigner/runs/run-formatters';
import {
  useTestSuites,
  useTestSuite,
  useArchiveTestSuite,
  useCreateDefaultTestSuite,
  useRunTestSuite,
  RunNativeSuiteDialog,
  type SuiteLastRun,
  type TestSuiteListItem,
} from '../../../features/test-suites';
import { CreateSuiteDialog } from './CreateSuiteDialog';
import { SuiteScheduledSection } from './SuiteScheduledSection';
import {
  externalSuiteRunBlocker,
  externalSuiteSourceLabel,
} from './external/externalSuitePresentation';

const TERMINAL_RUN_LABELS: Partial<Record<StatusName, string>> = {
  success: 'Passed',
  failed: 'Failed',
  running: 'Running',
  queued: 'Queued',
  canceled: 'Canceled',
  timeout: 'Timed out',
  skipped: 'Skipped',
};

/** The status pill + tooltip for a native (layered-workflow) last run. */
function nativeRunPresentation(lastRun: Extract<SuiteLastRun, { kind: 'native' }>) {
  const isTerminal =
    lastRun.status === 'completed' ||
    lastRun.status === 'completed_with_errors' ||
    lastRun.status === 'completed_with_inconclusive';
  const statusName: StatusName = isTerminal
    ? lastRun.status === 'completed_with_inconclusive'
      ? 'skipped'
      : lastRun.failedWorkflows === 0
        ? 'success'
        : 'failed'
    : normalizeRunStatus(lastRun.status);
  return {
    statusName,
    // `completed_with_inconclusive` collapses to the neutral `skipped` color, but
    // it does not mean "skipped" — the platform (not the app under test) failed
    // before the suite could reach a verdict. Label it explicitly so it is not
    // read off the shared color key, which external runs also collapse into.
    label: lastRun.status === 'completed_with_inconclusive' ? 'Platform error' : undefined,
    title: isTerminal
      ? `${lastRun.successfulWorkflows} passed · ${lastRun.failedWorkflows} failed`
      : undefined,
  };
}

/**
 * The same, for an external run.
 *
 * `flaky` counts toward passing rather than failing: a test that passed on a
 * retry did pass, and folding it into the failure count would make every
 * retry-configured suite look permanently broken. `resultsTruncated` outranks
 * everything — a partial run that happens to contain no failures is not a pass.
 */
function externalRunPresentation(lastRun: Extract<SuiteLastRun, { kind: 'external' }>) {
  const isTerminal = lastRun.status === 'sealed';
  const statusName: StatusName = lastRun.resultsTruncated
    ? 'skipped'
    : isTerminal
      ? lastRun.failed === 0
        ? 'success'
        : 'failed'
      : normalizeRunStatus(lastRun.status);
  const counts = `${lastRun.passed} passed · ${lastRun.failed} failed${
    lastRun.flaky > 0 ? ` · ${lastRun.flaky} flaky` : ''
  }`;
  return {
    statusName,
    // Also collapses to `skipped`, but for a different reason than a native
    // platform error — the results were truncated, so the verdict is partial.
    label: lastRun.resultsTruncated ? 'Partial' : undefined,
    title: lastRun.resultsTruncated
      ? `Partial results — ${counts}`
      : isTerminal
        ? counts
        : undefined,
  };
}

/**
 * Compact last-run summary for a suite row: a pass/fail status pill plus the
 * relative time of the run, linking to the run detail. Blue/orange throughout —
 * red is reserved for destructive actions.
 *
 * Branches on `lastRun.kind` because the two land on different routes; a shared
 * flat shape would have linked an external run to a native detail page that
 * does not exist for it.
 */
function LastRunSummary({ lastRun }: { lastRun: SuiteLastRun | null }) {
  if (!lastRun) {
    return (
      <Text size="xs" className="text-muted-foreground italic shrink-0">
        Never run
      </Text>
    );
  }

  const {
    statusName,
    label: explicitLabel,
    title,
  } = lastRun.kind === 'native' ? nativeRunPresentation(lastRun) : externalRunPresentation(lastRun);
  const label = explicitLabel ?? TERMINAL_RUN_LABELS[statusName];
  const when = formatRelativeTime(lastRun.finishedAt ?? lastRun.startedAt ?? lastRun.createdAt);
  const className = 'flex items-center gap-2 shrink-0 hover:opacity-80';
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const body = (
    <>
      <StatusBadge status={statusName} label={label} size="sm" />
      <Text size="xs" className="text-muted-foreground">
        {when}
      </Text>
    </>
  );

  return lastRun.kind === 'native' ? (
    <Link
      to="/runs/test/$executionId"
      params={{ executionId: lastRun.executionId }}
      className={className}
      onClick={stop}
      title={title}
    >
      {body}
    </Link>
  ) : (
    <Link
      to="/runs/external/$runId"
      params={{ runId: lastRun.runId }}
      className={className}
      onClick={stop}
      title={title}
    >
      {body}
    </Link>
  );
}

/**
 * Can this suite be run right now?
 *
 * A native suite needs at least one workflow. An external suite needs a runner
 * with a non-empty `.env` mapping — the server refuses otherwise
 * (`EXTERNAL_TEST_RUNNER_NOT_CONFIGURED`), and a Run button that always produces
 * the same error is worse than a disabled one that says why.
 */
function runBlocker(suite: TestSuiteListItem): string | null {
  if (suite.kind === 'external') {
    if (!suite.externalRunner) return 'This suite has no runner configured';
    return externalSuiteRunBlocker(suite.externalRunner);
  }
  return suite.workflowCount === 0 ? 'Add at least one test to this suite' : null;
}

export function SuitesListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const suitesQuery = useTestSuites();
  const archive = useArchiveTestSuite();
  const runSuite = useRunTestSuite();
  const createDefault = useCreateDefaultTestSuite();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmArchive, setConfirmArchive] = React.useState<{ id: string; name: string } | null>(
    null
  );
  const [runTarget, setRunTarget] = React.useState<TestSuiteListItem | null>(null);
  const runTargetQuery = useTestSuite(runTarget?.kind === 'native' ? runTarget.id : undefined);

  useBreadcrumbs([
    { key: 'suites', label: 'Suites', href: '/runs/test' },
    { key: 'list', label: 'Configuration' },
  ]);

  const openBuilder = (suiteId: string) =>
    navigate({ to: '/suites/$suiteId', params: { suiteId } });

  const startSuiteRun = async (suite: TestSuiteListItem, reEnableDegraded = false) => {
    try {
      const result = await runSuite.mutateAsync({ id: suite.id, reEnableDegraded });
      setRunTarget(null);
      // The two kinds land on different detail routes, which is exactly why the
      // response is discriminated rather than flat.
      if (result.kind === 'external') {
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
        return;
      }
      navigate({
        to: '/runs/test/$executionId',
        params: { executionId: result.executionId },
      });
    } catch (err) {
      // Includes `EXTERNAL_TEST_RUN_IN_PROGRESS`, whose catalog message already
      // says a run is in flight and to wait or cancel it.
      toast.error(err instanceof Error ? err.message : 'Failed to run suite');
    }
  };

  const handleRun = (suite: TestSuiteListItem) => {
    if (suite.kind === 'native') {
      setRunTarget(suite);
      return;
    }
    void startSuiteRun(suite);
  };

  const confirmArchiveSuite = async () => {
    if (!confirmArchive) return;
    try {
      await archive.mutateAsync(confirmArchive.id);
      setConfirmArchive(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive suite');
    }
  };

  const handleCreateDefault = async () => {
    try {
      const { created } = await createDefault.mutateAsync();
      toast.success(created ? 'Default suite created' : 'Default suite already exists');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create the default suite');
    }
  };

  // Offered only once the list has loaded and no native suite is the default.
  const needsDefaultSuite =
    suitesQuery.data !== undefined &&
    !suitesQuery.data.some((suite) => suite.kind === 'native' && suite.isDefault);

  const newButton = () => (
    <Flex align="center" gap="sm">
      {needsDefaultSuite && (
        <Button
          size="sm"
          variant="secondary"
          isDisabled={createDefault.isPending}
          onPress={() => void handleCreateDefault()}
        >
          Create default suite
        </Button>
      )}
      <Button
        size="sm"
        variant="primary"
        iconLeft={<Plus size={14} weight="bold" />}
        onPress={() => setDialogOpen(true)}
      >
        New Suite
      </Button>
    </Flex>
  );

  return (
    <PageLayout>
      <SuiteScheduledSection />
      {suitesQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : suitesQuery.data && suitesQuery.data.length > 0 ? (
        <ListCard
          title="Suites"
          titleAccessory={`${suitesQuery.data.length} suite${
            suitesQuery.data.length === 1 ? '' : 's'
          }`}
          action={newButton()}
        >
          {suitesQuery.data.map((suite, index) => {
            const isExternal = suite.kind === 'external';
            const runner = suite.externalRunner;
            const isUploaded = runner?.sourceKind === 'upload';
            const blocker = runBlocker(suite);
            return (
              <ListCardRow
                key={suite.id}
                index={index}
                icon={
                  isExternal ? (
                    isUploaded ? (
                      <UploadSimple size={16} className="text-muted-foreground" />
                    ) : (
                      <GithubLogo size={16} className="text-muted-foreground" />
                    )
                  ) : (
                    <Stack size={16} className="text-muted-foreground" />
                  )
                }
                title={
                  <button
                    type="button"
                    onClick={() => openBuilder(suite.id)}
                    className="truncate text-left hover:underline"
                  >
                    {suite.name}
                  </button>
                }
                badges={
                  <>
                    {suite.isDefault && (
                      <TagBadge
                        name="Default"
                        color="blue"
                        size="sm"
                        hideDot
                        tooltip="Promoted flows are always placed in this suite"
                      />
                    )}
                    {isExternal && (
                      <TagBadge
                        name={suite.externalRunner?.provider ?? 'external'}
                        color="teal"
                        size="sm"
                        hideDot
                        tooltip="Your own test suite, run on Cofactor infrastructure"
                      />
                    )}
                    {!isExternal && suite.failureMode === 'stop_on_layer_failure' && (
                      <TagBadge name="stop on failure" color="orange" size="sm" hideDot />
                    )}
                  </>
                }
                meta={
                  <>
                    {isExternal ? (
                      // An external suite has no members to count — its contents
                      // are its repository — so the branch is the honest summary.
                      <span className="truncate">
                        {runner ? externalSuiteSourceLabel(runner) : 'No runner configured'}
                      </span>
                    ) : (
                      <span>
                        {suite.workflowCount} workflow{suite.workflowCount === 1 ? '' : 's'} ·{' '}
                        {suite.layerCount} layer{suite.layerCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {suite.description && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="truncate">{suite.description}</span>
                      </>
                    )}
                  </>
                }
                actions={
                  <Flex align="center" gap="sm">
                    <LastRunSummary lastRun={suite.lastRun} />
                    <Button
                      size="sm"
                      variant="primary"
                      iconLeft={<Play size={14} weight="fill" />}
                      isDisabled={blocker !== null || runSuite.isPending}
                      tooltip={blocker ?? undefined}
                      onPress={() => handleRun(suite)}
                    >
                      Run
                    </Button>
                    <MenuTrigger>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1.5 text-foreground/70 hover:text-foreground hover:bg-muted"
                        aria-label={`Actions for ${suite.name}`}
                      >
                        <DotsThreeVertical size={16} weight="bold" />
                      </Button>
                      <Menu
                        onAction={(key) => {
                          if (key === 'edit') openBuilder(suite.id);
                          if (key === 'archive')
                            setConfirmArchive({ id: suite.id, name: suite.name });
                        }}
                      >
                        <MenuItem id="edit">
                          <PencilSimple size={14} className="mr-2" />
                          Edit
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem id="archive" destructive>
                          <Trash size={14} className="mr-2" />
                          Archive
                        </MenuItem>
                      </Menu>
                    </MenuTrigger>
                  </Flex>
                }
              />
            );
          })}
        </ListCard>
      ) : (
        <EmptyState
          icon={<Stack size={48} />}
          title="No test suites yet"
          description="A test suite runs a curated set of workflows in a defined order. Arrange workflows into layers — layers run one after another, and workflows in the same layer run in parallel."
          action={newButton()}
        />
      )}

      <CreateSuiteDialog isOpen={dialogOpen} onOpenChange={setDialogOpen} onCreated={openBuilder} />

      <RunNativeSuiteDialog
        isOpen={runTarget?.kind === 'native'}
        onOpenChange={(open) => {
          if (!open) setRunTarget(null);
        }}
        suiteName={runTarget?.name ?? 'suite'}
        members={runTargetQuery.data?.members ?? []}
        isLoading={runTargetQuery.isLoading}
        isPending={runSuite.isPending}
        onConfirm={(reEnableDegraded) => {
          if (runTarget) void startSuiteRun(runTarget, reEnableDegraded);
        }}
      />

      <DialogTrigger
        isOpen={confirmArchive !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmArchive(null);
        }}
        size="md"
      >
        <span />
        <Dialog title="Archive suite?">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              Archive{' '}
              <Text as="span" weight="semibold" className="text-foreground">
                {confirmArchive?.name ?? 'this suite'}
              </Text>
              ? It will be hidden from the list. Run history is retained.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="secondary" onPress={() => setConfirmArchive(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              isDisabled={archive.isPending}
              onPress={() => void confirmArchiveSuite()}
            >
              Archive
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </PageLayout>
  );
}
